import React from 'react';
import SEO from '../components/SEO.jsx';

const Terms = () => {
  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO
        title="Terms & Conditions"
        description="Terms and conditions for visits to Bioresonance Leicester, 108 New Walk, Leicester. Regulations for appointments, deposits and clinic use."
        path="/terms"
      />
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
            Terms & Conditions
          </h1>
          <h2 className="text-2xl font-semibold text-dark-grey mb-2">
            Regulations for Visits
          </h2>
          <p className="text-lg text-gray-600">
            TP Wellness
          </p>
          <p className="text-gray-600 mt-1">
            108 New Walk, 3rd Floor, Room 303, Leicester, LE1 7EA
          </p>
        </header>

        <article className="space-y-10 text-dark-grey">
          {/* §1 General Provisions */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§1 General Provisions</h2>
            <p className="mb-4">
              These regulations, hereinafter referred to as the &ldquo;Visit Regulations&rdquo;, define the rules for using the services provided at the TP Wellness, located at 108 New Walk, 3rd Floor, Room 303, Leicester, LE1 7EA, hereinafter referred to as the &ldquo;Clinic&rdquo;.
            </p>
            <p className="mb-4">
              The Clinic provides services:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Monday and Tuesday: Closed</li>
              <li>Wednesday to Friday: 9:30 am – 8:00 pm</li>
              <li>Saturday and Sunday: 8:00 am – 8:00 pm</li>
            </ul>
            <p className="mb-4">
              Services may also be provided at other times by individual arrangement with the Client.
            </p>
            <p className="mb-4">
              Clients are required to familiarise themselves with these Visit Regulations before making a reservation for any service, hereinafter referred to as the &ldquo;Service&rdquo;.
            </p>
            <p className="mb-4">
              These Visit Regulations, together with the service description, constitute an agreement for the provision of services.
            </p>
            <p className="mb-4">
              The Clinic provides services to individuals over 18 years of age.
            </p>
            <p className="mb-4">
              Individuals under 18 years of age may use the services of the Clinic only with the explicit consent of a parent or legal guardian and under their supervision throughout the appointment.
            </p>
            <p className="mb-4">
              To confirm an appointment and conclude an agreement, the Client is required to pay a deposit in an amount agreed with the Service Provider.
            </p>
            <p className="mb-4">
              The Client may cancel an appointment free of charge up to 24 hours before the scheduled appointment.
            </p>
            <p className="mb-4">
              Failure to attend an appointment without timely cancellation, late arrival preventing the service from being provided within the reserved time, or cancellation later than 24 hours before the appointment will result in the deposit being retained by the Service Provider.
            </p>
            <p className="mb-4">
              Smoking and the use of tobacco or nicotine products, including electronic cigarettes, as well as the consumption of alcohol or intoxicating substances, are strictly prohibited on the Clinic premises and within the building.
            </p>
            <p>
              Animals are not permitted on the premises of the building where the Clinic is located.
            </p>
          </section>

          {/* §2 Definitions */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§2 Definitions</h2>
            <dl className="space-y-2">
              <div><dt className="font-semibold inline">Client</dt><dd className="inline"> – a natural person who enters into an agreement with the Service Provider for purposes not directly related to their business or professional activity.</dd></div>
              <div><dt className="font-semibold inline">Child / Minor</dt><dd className="inline"> – a person under 18 years of age using the Clinic&rsquo;s services with parental or legal guardian consent and supervision.</dd></div>
              <div><dt className="font-semibold inline">Visit Regulations</dt><dd className="inline"> – these regulations governing the provision of services.</dd></div>
              <div><dt className="font-semibold inline">Clinic</dt><dd className="inline"> – the premises located at 108 New Walk, 3rd Floor, Room 303, Leicester, LE1 7EA.</dd></div>
              <div><dt className="font-semibold inline">Service / Therapy</dt><dd className="inline"> – services provided by the Service Provider to the Client.</dd></div>
              <div><dt className="font-semibold inline">Agreement</dt><dd className="inline"> – an agreement concluded between the Client and the Service Provider.</dd></div>
              <div><dt className="font-semibold inline">Order / Reservation</dt><dd className="inline"> – a Client&rsquo;s declaration of intent to conclude an agreement.</dd></div>
              <div><dt className="font-semibold inline">Deposit</dt><dd className="inline"> – a payment securing a reservation for a specific appointment.</dd></div>
              <div><dt className="font-semibold inline">Service Provider</dt><dd className="inline"> – TP Wellness, operating at the above address.</dd></div>
            </dl>
          </section>

          {/* §3 Clients */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§3 Clients</h2>
            <p className="mb-4">
              Clinic services are intended primarily for adults.
            </p>
            <p className="mb-4">
              Minors may receive services only with documented consent and supervision of a parent or legal guardian. The Clinic reserves the right to request such consent and refuse service if it is not provided.
            </p>
            <p>
              Clients are required to provide accurate information regarding their health where necessary and to sign declarations confirming the accuracy of the information provided.
            </p>
          </section>

          {/* §4 Provided Services */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§4 Provided Services</h2>
            <p className="mb-4">
              The Clinic provides services listed in the current price list available on{' '}
              <a href="https://tpwellness.co.uk" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-medium">https://tpwellness.co.uk</a>
              {' '}or at the Clinic.
            </p>
            <p className="mb-4">
              Clients should familiarise themselves with the description, duration, and price of services before booking.
            </p>
            <p className="mb-4">
              The Service Provider informs Clients of the nearest available appointment date prior to booking.
            </p>
            <p className="mb-4">
              Repeated late cancellations or failures to attend appointments may result in refusal of further bookings.
            </p>
            <p className="mb-4">
              Therapy Packages are valid for a specified period and are non-refundable, non-transferable, and cannot be exchanged for cash.
            </p>
            <p>
              Failure to attend or cancel a therapy included in a package within the required timeframe results in forfeiture of that session.
            </p>
          </section>

          {/* §5 Booking Appointments and Conclusion of Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§5 Booking Appointments and Conclusion of Agreement</h2>
            <p className="mb-4">
              Appointments booked online require full payment at the time of booking. Payment of the full session fee confirms the reservation and constitutes acceptance of these Visit Regulations.
            </p>
            <p className="mb-4">
              The Client may cancel or reschedule an appointment free of charge up to 24 hours before the scheduled appointment time. In such cases, the full amount paid will be refunded within up to 7 working days using the original payment method.
            </p>
            <p className="mb-4">
              If an appointment is cancelled less than 24 hours before the scheduled time, the payment is non-refundable.
            </p>
            <p>
              Failure to attend an appointment without prior cancellation will be treated as a late cancellation and no refund will be issued.
            </p>
          </section>

          {/* §6 Delays */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§6 Delays</h2>
            <p className="mb-4">
              Clients should arrive 5–10 minutes before the scheduled appointment.
            </p>
            <p className="mb-4">
              Delays must be reported at least one hour in advance where possible.
            </p>
            <p className="mb-4">
              Delays of up to 15 minutes may be accepted without shortening the service.
            </p>
            <p>
              Delays exceeding 15 minutes may result in refusal of service and forfeiture of the deposit.
            </p>
          </section>

          {/* §7 Payments */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§7 Payments</h2>
            <p className="mb-4">
              Clients booking services online are required to pay the full session fee at the time of booking.
            </p>
            <p className="mb-4">
              Prices are gross prices and include all applicable taxes.
            </p>
            <p className="mb-2">Accepted payment methods include:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Cash</li>
              <li>Card</li>
              <li>Bank transfer</li>
              <li>Electronic payment link</li>
              <li>Online payment</li>
            </ul>
            <p className="mb-4">
              Refunds, where applicable, are processed within up to 7 working days.
            </p>
            <p>
              Receipts or invoices are issued after completion of the service or upon request.
            </p>
          </section>

          {/* §8 Clinic Work Standards */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§8 Clinic Work Standards</h2>
            <p className="mb-4">
              Clinic staff are appropriately trained and certified.
            </p>
            <p>
              Hygiene and safety procedures are strictly followed before, during, and after each service.
            </p>
          </section>

          {/* §9 Medical Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§9 Medical Disclaimer</h2>
            <p className="mb-4">
              The services provided by the Clinic are complementary and non-medical in nature.
            </p>
            <p className="mb-4">
              Bioresonance therapy does not constitute medical advice, diagnosis, or treatment and does not replace consultation with a qualified medical professional.
            </p>
            <p>
              The Clinic does not guarantee specific health outcomes or results.
            </p>
          </section>

          {/* §10 Service Provider Obligations */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§10 Service Provider Obligations</h2>
            <p className="mb-2">The Service Provider is obliged to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>conduct a client consultation,</li>
              <li>inform clients of contraindications and recommendations,</li>
              <li>maintain hygiene and safety standards.</li>
            </ul>
          </section>

          {/* §11 Left Items */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§11 Left Items</h2>
            <p className="mb-4">
              Clients may use designated areas for personal belongings.
            </p>
            <p className="mb-4">
              The Clinic is not responsible for high-value items.
            </p>
            <p>
              Left items will be stored until collected by the Client.
            </p>
          </section>

          {/* §12 Prohibition of Photography and Recording */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§12 Prohibition of Photography and Recording</h2>
            <p className="mb-4">
              Photography and audio/video recording are strictly prohibited throughout the Clinic.
            </p>
            <p className="mb-4">
              Exceptions require prior written consent from the Service Provider.
            </p>
            <p>
              Violations may result in refusal of service and legal action.
            </p>
          </section>

          {/* §13 Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>§13 Governing Law</h2>
            <p>
              These Visit Regulations are governed by and interpreted in accordance with the laws of England and Wales.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default Terms;
